import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, TrendingDown, Target, Clock, AlertTriangle, 
  CheckCircle2, XCircle, Minus, RefreshCw, Calendar, Filter,
  BarChart3, PieChart, Activity
} from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { bookingsAPI } from '../../services/api';
import { toast } from 'sonner';

/**
 * EstimateAccuracyDashboard Component
 * Shows time estimate accuracy trends and statistics for the owner
 */
const EstimateAccuracyDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');
  const [serviceType, setServiceType] = useState('all');

  useEffect(() => {
    fetchStats();
  }, [days, serviceType]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const params = { days: parseInt(days) };
      if (serviceType !== 'all') {
        params.service_type = serviceType;
      }
      const response = await bookingsAPI.getTimeAccuracyStats(params);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch accuracy stats:', error);
      toast.error('Failed to load accuracy data');
    } finally {
      setLoading(false);
    }
  };

  const formatMinutes = (minutes) => {
    if (!minutes && minutes !== 0) return '-';
    const absMin = Math.abs(minutes);
    const hours = Math.floor(absMin / 60);
    const mins = Math.round(absMin % 60);
    const sign = minutes > 0 ? '+' : minutes < 0 ? '-' : '';
    if (hours === 0) return `${sign}${mins}m`;
    if (mins === 0) return `${sign}${hours}h`;
    return `${sign}${hours}h ${mins}m`;
  };

  const getVarianceColor = (variance) => {
    if (Math.abs(variance) <= 15) return 'text-green-500';
    if (Math.abs(variance) <= 30) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getAccuracyColor = (rate) => {
    if (rate >= 80) return 'text-green-500';
    if (rate >= 60) return 'text-yellow-500';
    return 'text-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 text-[var(--primary)] animate-spin" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="text-center py-12 text-[var(--text-muted)]">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>Unable to load accuracy data</p>
      </div>
    );
  }

  const { overall_accuracy, by_service_type, trends, top_variances, total_jobs_analyzed } = stats;

  return (
    <div className="space-y-6" data-testid="estimate-accuracy-dashboard">
      {/* Header with Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[var(--text-primary)] flex items-center gap-2">
            <Activity className="w-6 h-6 text-[var(--primary)]" />
            Estimate Accuracy Tracking
          </h2>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Analyzing {total_jobs_analyzed} completed jobs
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[130px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={serviceType} onValueChange={setServiceType}>
            <SelectTrigger className="w-[140px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              <SelectItem value="rides">Rides</SelectItem>
              <SelectItem value="laundry">Laundry</SelectItem>
              <SelectItem value="courier">Courier</SelectItem>
              <SelectItem value="errands">Errands</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="icon" onClick={fetchStats}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {total_jobs_analyzed === 0 ? (
        <div className="text-center py-12 bg-[var(--background-paper)] rounded-xl border border-[var(--border-primary)]">
          <Clock className="w-16 h-16 mx-auto mb-4 text-[var(--text-muted)] opacity-50" />
          <h3 className="text-lg font-semibold text-[var(--text-primary)]">No Data Yet</h3>
          <p className="text-[var(--text-muted)] mt-2">
            Complete some jobs with recorded actual times to see accuracy statistics.
          </p>
        </div>
      ) : (
        <>
          {/* Overall Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Accuracy Rate */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[var(--background-paper)] rounded-xl p-5 border border-[var(--border-primary)]"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[var(--text-muted)]">Accuracy Rate</span>
                <Target className="w-5 h-5 text-[var(--primary)]" />
              </div>
              <p className={`text-3xl font-bold ${getAccuracyColor(overall_accuracy.accuracy_rate)}`}>
                {overall_accuracy.accuracy_rate}%
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Within 15% of actual time
              </p>
            </motion.div>

            {/* Average Variance */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[var(--background-paper)] rounded-xl p-5 border border-[var(--border-primary)]"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[var(--text-muted)]">Avg Variance</span>
                {overall_accuracy.average_variance_minutes > 0 ? (
                  <TrendingUp className="w-5 h-5 text-red-500" />
                ) : overall_accuracy.average_variance_minutes < 0 ? (
                  <TrendingDown className="w-5 h-5 text-green-500" />
                ) : (
                  <Minus className="w-5 h-5 text-gray-400" />
                )}
              </div>
              <p className={`text-3xl font-bold ${getVarianceColor(overall_accuracy.average_variance_minutes)}`}>
                {formatMinutes(overall_accuracy.average_variance_minutes)}
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                {overall_accuracy.average_variance_minutes > 0 ? 'Underestimating' : 
                 overall_accuracy.average_variance_minutes < 0 ? 'Overestimating' : 'On target'}
              </p>
            </motion.div>

            {/* Underestimates */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-[var(--background-paper)] rounded-xl p-5 border border-[var(--border-primary)]"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[var(--text-muted)]">Underestimates</span>
                <TrendingUp className="w-5 h-5 text-orange-500" />
              </div>
              <p className="text-3xl font-bold text-orange-500">
                {overall_accuracy.underestimate_count}
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Jobs took longer than expected
              </p>
            </motion.div>

            {/* Overestimates */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[var(--background-paper)] rounded-xl p-5 border border-[var(--border-primary)]"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm text-[var(--text-muted)]">Overestimates</span>
                <TrendingDown className="w-5 h-5 text-blue-500" />
              </div>
              <p className="text-3xl font-bold text-blue-500">
                {overall_accuracy.overestimate_count}
              </p>
              <p className="text-xs text-[var(--text-muted)] mt-1">
                Jobs finished earlier than expected
              </p>
            </motion.div>
          </div>

          {/* Accuracy by Service Type */}
          {Object.keys(by_service_type).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-[var(--background-paper)] rounded-xl p-5 border border-[var(--border-primary)]"
            >
              <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-[var(--primary)]" />
                Accuracy by Service Type
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {Object.entries(by_service_type).map(([type, data]) => (
                  <div 
                    key={type}
                    className="bg-[var(--background-subtle)] rounded-lg p-4"
                  >
                    <h4 className="font-medium text-[var(--text-primary)] capitalize mb-2">
                      {type}
                    </h4>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[var(--text-muted)]">Jobs:</span>
                        <span className="font-medium">{data.count}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--text-muted)]">Accuracy:</span>
                        <span className={`font-medium ${getAccuracyColor(data.accuracy_rate)}`}>
                          {data.accuracy_rate}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--text-muted)]">Avg Var:</span>
                        <span className={`font-medium ${getVarianceColor(data.average_variance)}`}>
                          {formatMinutes(data.average_variance)}
                        </span>
                      </div>
                    </div>
                    {/* Mini progress bar */}
                    <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${data.accuracy_rate >= 80 ? 'bg-green-500' : data.accuracy_rate >= 60 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{ width: `${data.accuracy_rate}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Weekly Trends */}
          {trends.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-[var(--background-paper)] rounded-xl p-5 border border-[var(--border-primary)]"
            >
              <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-[var(--primary)]" />
                Weekly Accuracy Trends
              </h3>
              <div className="overflow-x-auto">
                <div className="flex gap-2 min-w-max pb-2">
                  {trends.map((week, idx) => (
                    <div 
                      key={week.week}
                      className="flex flex-col items-center min-w-[80px]"
                    >
                      {/* Bar */}
                      <div className="h-24 w-full flex items-end justify-center mb-2">
                        <div 
                          className={`w-10 rounded-t-lg transition-all ${
                            week.accuracy_rate >= 80 ? 'bg-green-500' : 
                            week.accuracy_rate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                          }`}
                          style={{ height: `${Math.max(10, week.accuracy_rate)}%` }}
                        />
                      </div>
                      {/* Label */}
                      <span className="text-xs text-[var(--text-muted)]">
                        {week.week.split('-W')[1] ? `W${week.week.split('-W')[1]}` : week.week}
                      </span>
                      <span className="text-xs font-medium text-[var(--text-secondary)]">
                        {week.accuracy_rate}%
                      </span>
                      <span className="text-xs text-[var(--text-muted)]">
                        ({week.jobs_count} jobs)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* Top Variances (Biggest Misses) */}
          {top_variances.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-[var(--background-paper)] rounded-xl p-5 border border-[var(--border-primary)]"
            >
              <h3 className="font-semibold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Biggest Estimation Misses
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--border-primary)]">
                      <th className="text-left py-2 px-3 text-[var(--text-muted)]">Service</th>
                      <th className="text-left py-2 px-3 text-[var(--text-muted)]">Type</th>
                      <th className="text-right py-2 px-3 text-[var(--text-muted)]">Estimated</th>
                      <th className="text-right py-2 px-3 text-[var(--text-muted)]">Actual</th>
                      <th className="text-right py-2 px-3 text-[var(--text-muted)]">Variance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top_variances.slice(0, 5).map((item, idx) => (
                      <tr 
                        key={item.booking_id || idx}
                        className="border-b border-[var(--border-primary)]/50 hover:bg-[var(--background-subtle)]"
                      >
                        <td className="py-2 px-3 font-medium">{item.service_name || 'Unknown'}</td>
                        <td className="py-2 px-3 capitalize text-[var(--text-secondary)]">{item.service_type}</td>
                        <td className="py-2 px-3 text-right">{formatMinutes(item.estimated)}</td>
                        <td className="py-2 px-3 text-right">{formatMinutes(item.actual)}</td>
                        <td className={`py-2 px-3 text-right font-medium ${item.variance > 0 ? 'text-red-500' : 'text-green-500'}`}>
                          {formatMinutes(item.variance)} ({item.variance_percent}%)
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          )}

          {/* Summary Insights */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-gradient-to-r from-[var(--primary)]/10 to-[var(--primary)]/5 rounded-xl p-5 border border-[var(--primary)]/30"
          >
            <h3 className="font-semibold text-[var(--text-primary)] mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[var(--primary)]" />
              Insights
            </h3>
            <ul className="space-y-2 text-sm text-[var(--text-secondary)]">
              {overall_accuracy.accuracy_rate >= 80 && (
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                  Your estimates are highly accurate with {overall_accuracy.accuracy_rate}% within target range.
                </li>
              )}
              {overall_accuracy.average_variance_minutes > 15 && (
                <li className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                  Jobs are taking {formatMinutes(overall_accuracy.average_variance_minutes)} longer on average. Consider adding buffer time.
                </li>
              )}
              {overall_accuracy.average_variance_minutes < -15 && (
                <li className="flex items-start gap-2">
                  <TrendingDown className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                  Jobs are finishing {formatMinutes(Math.abs(overall_accuracy.average_variance_minutes))} early on average. Estimates may be too generous.
                </li>
              )}
              {Object.entries(by_service_type).some(([_, data]) => data.accuracy_rate < 60) && (
                <li className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                  Some service types have low accuracy. Review the breakdown above for improvement areas.
                </li>
              )}
            </ul>
          </motion.div>
        </>
      )}
    </div>
  );
};

export default EstimateAccuracyDashboard;
